import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  getQuickBooksInvoiceRoutingURL,
  saveQuickBooksInvoiceRoutingURL,
} from "../../../utils/urls";

const CATEGORY_LABELS = {
  rent_lease: "Rent / Lease invoices",
  security_deposit: "Security deposits",
  property_management: "Property management fees",
  service_charge: "Service charge",
  levy_contract: "Levy invoices",
  utility_power: "Power (utility) invoices",
  utility_water: "Water (utility) invoices",
};

// Which connected QuickBooks realm each invoice category posts to. Item and
// account mappings are per realm, so a category must be routed to the same
// realm where its Item is mapped.
const QuickBooksRoutingModal = ({ visible, onHide, facilityId, connections }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const realmOptions = (connections || []).map((c) => ({
    value: c._id,
    label: `${c.label} (${c.environment})`,
  }));

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await makeRequest2(getQuickBooksInvoiceRoutingURL(facilityId), "GET");
      if (response?.success && response.data?.success) {
        setRows(response.data.data?.routing || []);
      } else {
        setError(response?.data?.error || response?.error || "Failed to load routing");
      }
    } catch (err) {
      setError(err.message || "Failed to load routing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && facilityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, facilityId]);

  const setRealm = (invoiceCategory, qbConfigId) =>
    setRows((prev) => prev.map((r) => (r.invoiceCategory === invoiceCategory ? { ...r, qbConfigId } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const response = await makeRequest2(saveQuickBooksInvoiceRoutingURL(facilityId), "POST", {
        routing: rows.map((r) => ({ invoiceCategory: r.invoiceCategory, qbConfigId: r.qbConfigId || null })),
      });
      if (response?.success && response.data?.success) {
        const failed = response.data.data?.failed || [];
        if (failed.length) {
          toastify("error", failed.map((f) => f.error).join("; "));
        } else {
          toastify("success", "Invoice routing saved");
          onHide();
        }
      } else {
        toastify("error", response?.data?.error || response?.error || "Failed to save routing");
      }
    } catch (err) {
      toastify("error", err.message || "Failed to save routing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header="Invoice Routing"
      visible={visible}
      onHide={onHide}
      style={{ width: "90vw", maxWidth: "720px" }}
    >
      <p className="text-muted small mb-3">
        Choose which QuickBooks company each kind of invoice posts to. Leave a category on
        "Default" to use the facility's default/only realm. Map the matching Item under "Map
        Accounts &amp; Items" on the same realm you route it to.
      </p>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {loading ? (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
          Loading…
        </div>
      ) : (
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Invoice type</th>
              <th style={{ minWidth: 280 }}>Posts to QuickBooks company</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.invoiceCategory}>
                <td>{CATEGORY_LABELS[row.invoiceCategory] || row.invoiceCategory}</td>
                <td>
                  <Dropdown
                    value={row.qbConfigId || null}
                    options={realmOptions}
                    onChange={(e) => setRealm(row.invoiceCategory, e.value)}
                    placeholder="Default"
                    showClear
                    className="w-100"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-sm btn-light" onClick={onHide} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-sm btn-success" onClick={save} disabled={saving || loading}>
          {saving ? (
            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
          ) : (
            <i className="ti ti-device-floppy me-1"></i>
          )}
          Save Routing
        </button>
      </div>
    </Dialog>
  );
};

export default QuickBooksRoutingModal;
